/* eslint-disable i18next/no-literal-string -- i18n deferred to v2 per plan */
import { useEffect, useState } from 'react'
import {
  Button, Spinner, Label, Checkbox, Input,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  Tooltip, TooltipContent, TooltipTrigger, TooltipProvider,
} from '@agion/ui'
import { createOperation } from '@/features/operation/infra_operation.service'

// Stage names as defined by the backend
type SkippableStage = 'precheck' | 'postinst' | 'verify'
type FlywayMode = 'standard' | 'repair_then_migrate' | 'validate_only' | 'skip'

// Form state shape
interface HostUpgradeFormState {
  flywayMode: FlywayMode
  dryRun: boolean
  skipStages: Record<SkippableStage, boolean>
  skipReasons: Record<SkippableStage, string>
}

// Backend payload shape (U1 contract) - extends Record<string, unknown> for compatibility with createOperation
type HostUpgradeParameters = Record<string, unknown> & {
  schemaVersion: 1
  flywayMode: FlywayMode
  skipStages: SkippableStage[]
  dryRun: boolean
  skipReasons: Record<string, string>
}

interface Props {
  open: boolean
  environmentId: string
  onClose: () => void
  onSuccess: (operationId: string) => void
}

const DEFAULT_FORM_STATE: HostUpgradeFormState = {
  flywayMode: 'repair_then_migrate',
  dryRun: false,
  skipStages: { precheck: false, postinst: false, verify: false },
  skipReasons: { precheck: '', postinst: '', verify: '' },
}

const FLYWAY_MODE_LABELS: Record<FlywayMode, string> = {
  repair_then_migrate: 'Repair then migrate (recommended for old / drifted hosts)',
  standard: 'Standard migrate only (clean hosts)',
  validate_only: 'Validate only (no DDL)',
  skip: 'Skip Flyway entirely',
}

interface StageInfo {
  key: 'precheck' | 'apt' | 'postinst' | 'verify'
  label: string
  description: string
  canSkip: boolean
}

const STAGE_INFO: StageInfo[] = [
  { key: 'precheck', label: 'Pre-check', description: 'Disk space, apt lock, flags file write', canSkip: true },
  { key: 'apt', label: 'APT', description: 'apt-get update + install', canSkip: false },
  { key: 'postinst', label: 'Postinst / Flyway', description: 'Flyway migration + journal capture', canSkip: true },
  { key: 'verify', label: 'Verify', description: 'systemctl is-active checks', canSkip: true },
]

export function HostUpgradeDialog({ open, environmentId, onClose, onSuccess }: Props) {
  const [formState, setFormState] = useState<HostUpgradeFormState>(DEFAULT_FORM_STATE)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset all state when dialog opens (mirrors InfraEdgeRuntimeDialog)
  useEffect(() => {
    if (open) {
      setFormState(DEFAULT_FORM_STATE)
      setIsSubmitting(false)
      setError(null)
    }
  }, [open])

  const handleSubmit = async () => {
    setIsSubmitting(true)
    setError(null)

    // Build skipStages array from the boolean map
    const skipStages = (Object.entries(formState.skipStages) as [SkippableStage, boolean][])
      .filter(([, enabled]) => enabled)
      .map(([stage]) => stage)

    // Build skipReasons from only the skipped stages
    const skipReasons: Record<string, string> = {}
    for (const stage of skipStages) {
      skipReasons[stage] = formState.skipReasons[stage]
    }

    const parameters: HostUpgradeParameters = {
      schemaVersion: 1,
      flywayMode: formState.flywayMode,
      skipStages,
      dryRun: formState.dryRun,
      skipReasons,
    }

    try {
      const op = await createOperation({
        environmentId,
        operationType: 'host_upgrade',
        parameters,
      })
      onSuccess(op.id)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create operation')
    } finally {
      setIsSubmitting(false)
    }
  }

  const setFlywayMode = (mode: FlywayMode) => {
    setFormState(prev => ({ ...prev, flywayMode: mode }))
  }

  const setDryRun = (dryRun: boolean) => {
    setFormState(prev => ({ ...prev, dryRun }))
  }

  const setSkipStage = (stage: SkippableStage, enabled: boolean) => {
    setFormState(prev => ({
      ...prev,
      skipStages: { ...prev.skipStages, [stage]: enabled },
      // Clear reason when disabling skip
      skipReasons: enabled ? prev.skipReasons : { ...prev.skipReasons, [stage]: '' },
    }))
  }

  const setSkipReason = (stage: SkippableStage, reason: string) => {
    setFormState(prev => ({
      ...prev,
      skipReasons: { ...prev.skipReasons, [stage]: reason },
    }))
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose() }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Upgrade Edge Host Packages</DialogTitle>
          <DialogDescription>
            Run apt-get upgrade on the Edge host with configurable Flyway mode and stage controls.
          </DialogDescription>
        </DialogHeader>

        <div className="py-3 space-y-4">
          {/* Primary controls: Flyway mode + Dry run */}
          <div className="space-y-3">
            <div>
              <Label htmlFor="flyway-mode" className="text-sm font-medium text-slate-700 dark:text-slate-300 block mb-1">
                Flyway Mode
              </Label>
              <Select value={formState.flywayMode} onValueChange={(v) => setFlywayMode(v as FlywayMode)}>
                <SelectTrigger id="flyway-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(FLYWAY_MODE_LABELS) as FlywayMode[]).map(mode => (
                    <SelectItem key={mode} value={mode}>
                      {FLYWAY_MODE_LABELS[mode]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={formState.dryRun}
                onCheckedChange={checked => setDryRun(checked === true)}
                aria-label="Dry run"
              />
              <span className="text-sm text-slate-700 dark:text-slate-300">
                Dry run (simulate only, no actual changes)
              </span>
            </Label>
          </div>

          {/* Stage cards */}
          <div className="space-y-3 pt-2 border-t border-slate-200 dark:border-slate-700">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Stages
            </h3>

            {STAGE_INFO.map(stage => {
              const isApt = stage.key === 'apt'
              const canSkip = stage.canSkip !== false
              const isSkipped = !isApt && formState.skipStages[stage.key as SkippableStage]

              return (
                <div
                  key={stage.key}
                  className="border border-slate-200 dark:border-slate-700 rounded-md p-3"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {stage.label}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        {stage.description}
                      </p>
                    </div>

                    {/* Skip toggle */}
                    {canSkip ? (
                      <Label className="flex items-center gap-1.5 cursor-pointer">
                        <Checkbox
                          checked={isSkipped}
                          onCheckedChange={checked => setSkipStage(stage.key as SkippableStage, checked === true)}
                          aria-label={`Skip ${stage.label}`}
                        />
                        <span className="text-xs text-slate-600 dark:text-slate-400">Skip</span>
                      </Label>
                    ) : (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Label className="flex items-center gap-1.5 cursor-not-allowed opacity-50">
                              <Checkbox
                                checked={false}
                                disabled
                                aria-label="APT is the operation; cannot be skipped"
                              />
                              <span className="text-xs text-slate-600 dark:text-slate-400">Skip</span>
                            </Label>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>APT is the operation; cannot be skipped</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>

                  {/* Reason input when skipped */}
                  {isSkipped && (
                    <div className="mt-2">
                      <Input
                        type="text"
                        placeholder="Reason for skipping (optional)"
                        value={formState.skipReasons[stage.key as SkippableStage]}
                        onChange={e => setSkipReason(stage.key as SkippableStage, e.target.value)}
                        aria-label={`${stage.label} skip reason`}
                        className="text-sm"
                      />
                    </div>
                  )}

                </div>
              )
            })}
          </div>

          {/* Error display */}
          {error && (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleSubmit()}
            disabled={isSubmitting}
          >
            {isSubmitting && <Spinner className="h-4 w-4 mr-1" />}
            {formState.dryRun ? 'Dry Run' : 'Upgrade'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
